import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage                                from './pages/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/ForgotPasswordPage';
import RegisterPage         from './pages/RegisterPage';
import SesizareAnonima      from './pages/SesizareAnonima';
import VerificaSesizare     from './pages/VerificaSesizare';
import DashboardPage       from './pages/DashboardPage';
import CereriListPage      from './pages/CereriListPage';
import CerereNovaPage      from './pages/CerereNovaPage';
import CerereDetaliuPage   from './pages/CerereDetaliuPage';
import AnalyticsPage       from './pages/AnalyticsPage';
import AdminPage           from './pages/AdminPage';
import FluxPage            from './pages/FluxPage';
import SemnaturePage       from './pages/SemnaturePage';
import PlatiPage           from './pages/PlatiPage';
import ProgramariPage      from './pages/ProgramariPage';
import ProfilPage          from './pages/ProfilPage';
import ConfirmaEmailPage      from './pages/ConfirmaEmailPage';
import CompleteProfilePage   from './pages/CompleteProfilePage';
import ImpozitePage          from './pages/ImpozitePage';
import FormularePage         from './pages/FormularePage';
import InfoUtilePage        from './pages/InfoUtilePage';
import GdprConsentModal     from './components/GdprConsentModal';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    }
  }
});

const ROLURI_FUNCTIONAR = ['functionar','sef_departament','admin','primar','superadmin'];

function PrivateRoute({ children, requireAdmin = false, requireFunctionar = false }) {
  const { user, loading, needsProfile, gdprConsented, acceptGdpr, logout } = useAuth();
  if (loading) return <div className="loading-screen">Se încarcă...</div>;
  if (!user)   return <Navigate to="/login" replace />;
  if (needsProfile) return <Navigate to="/complete-profile" replace />;
  if (requireAdmin && !['admin','primar','superadmin'].includes(user.rol)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (requireFunctionar && !ROLURI_FUNCTIONAR.includes(user.rol)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!gdprConsented) {
    return <GdprConsentModal onAccept={acceptGdpr} onLogout={logout} />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login"           element={<PublicRoute><LoginPage/></PublicRoute>}/>
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage/></PublicRoute>}/>
            <Route path="/reset-password"  element={<PublicRoute><ResetPasswordPage/></PublicRoute>}/>
            <Route path="/register"          element={<RegisterPage/>}/>
            <Route path="/confirma-email"    element={<ConfirmaEmailPage/>}/>
            <Route path="/complete-profile"  element={<CompleteProfilePage/>}/>
            <Route path="/sesizare-anonima"  element={<Navigate to="/register" replace/>}/>
            <Route path="/verifica-sesizare" element={<Navigate to="/register" replace/>}/>
            <Route path="/info"              element={<InfoUtilePage/>}/>

            <Route path="/dashboard"   element={<PrivateRoute><DashboardPage/></PrivateRoute>}/>
            <Route path="/cereri"      element={<PrivateRoute><CereriListPage/></PrivateRoute>}/>
            <Route path="/cereri/noua" element={<PrivateRoute><CerereNovaPage/></PrivateRoute>}/>
            <Route path="/cereri/:id"  element={<PrivateRoute><CerereDetaliuPage/></PrivateRoute>}/>
            <Route path="/analytics"   element={<PrivateRoute><AnalyticsPage/></PrivateRoute>}/>
            <Route path="/profil"      element={<PrivateRoute><ProfilPage/></PrivateRoute>}/>
            <Route path="/admin"       element={<PrivateRoute requireAdmin><AdminPage/></PrivateRoute>}/>
            <Route path="/cereri/:cerereId/flux" element={<PrivateRoute requireFunctionar><FluxPage/></PrivateRoute>}/>
            <Route path="/cereri/:cerereId/semneaza" element={<PrivateRoute><SemnaturePage/></PrivateRoute>}/>
            <Route path="/plati" element={<PrivateRoute><PlatiPage/></PrivateRoute>}/>
            <Route path="/plata-finalizata" element={<PrivateRoute><PlatiPage/></PrivateRoute>}/>
            <Route path="/programari" element={<PrivateRoute><ProgramariPage/></PrivateRoute>}/>
            <Route path="/impozite"   element={<PrivateRoute><ImpozitePage/></PrivateRoute>}/>
            <Route path="/formulare"  element={<PrivateRoute><FormularePage/></PrivateRoute>}/>

            <Route path="/"  element={<Navigate to="/dashboard" replace/>}/>
            <Route path="*"  element={<Navigate to="/dashboard" replace/>}/>
          </Routes>
        </BrowserRouter>

        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} closeOnClick pauseOnHover/>
      </AuthProvider>
    </QueryClientProvider>
  );
}